import { COLOURS } from "../data/colours";

/**
 * Row of Dulux Powdercoat colour swatches.
 * - onChange(colourKey) fires when a swatch is clicked.
 * - `selected` is the currently active key ("black" | "white" | ...).
 * - `size` controls the swatch diameter in px (default 16).
 * - Placement is inline flex; parent controls spacing.
 * - The "custom" option is rendered with a diagonal hatch to signal it doesn't
 *   change the visual, only the selection.
 */
export default function ColourSwatches({
  selected,
  onChange,
  size = 16,
  testIdPrefix = "swatch",
  compact = false,
  onPreview, // called with a colourKey while cursor is over a swatch, and null on leave
}) {
  return (
    <div
      data-testid={`${testIdPrefix}-group`}
      className={`flex flex-wrap items-center gap-1.5 ${compact ? "" : "gap-2"}`}
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={() => onPreview?.(null)}
    >
      {COLOURS.map((c) => {
        const isSelected = c.key === selected;
        const isCustom = c.key === "custom";
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`${testIdPrefix}-${c.key}`}
            aria-label={`Colour ${c.label}${isSelected ? ", selected" : ""}`}
            aria-pressed={isSelected}
            title={c.label}
            onMouseEnter={() => onPreview?.(c.key)}
            onFocus={() => onPreview?.(c.key)}
            onBlur={() => onPreview?.(null)}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(c.key);
            }}
            className={`relative flex shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
              isSelected
                ? "border-[#0a0a0c] ring-2 ring-offset-[3px] ring-[#0a0a0c]/80 ring-offset-white"
                : "border-[#0a0a0c]/20 hover:border-[#0a0a0c]/60"
            }`}
            style={{ width: size, height: size }}
          >
            {isCustom ? (
              <span
                aria-hidden
                className="block rounded-full"
                style={{
                  width: size - 4,
                  height: size - 4,
                  background:
                    "repeating-linear-gradient(45deg,#0a0a0c 0 2px,#f7f5f0 2px 4px)",
                }}
              />
            ) : (
              <span
                aria-hidden
                className="block rounded-full"
                style={{
                  width: size - 4,
                  height: size - 4,
                  background: c.hex,
                  boxShadow:
                    c.key === "white"
                      ? "inset 0 0 0 1px rgba(0,0,0,0.12)"
                      : undefined,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
